import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ExternalLink } from "lucide-react";

// Used by the Community Hub — a big rounded card that jumps to another page
// and optionally embeds it in an iframe when expanded. Every element the
// admin might want to hide/show is driven by config.
const NavCardEmbedBlock = ({ block }) => {
  const cfg = block.config || {};
  const isComingSoon = !!cfg.coming_soon;
  const [expanded, setExpanded] = useState(false);
  const accent = cfg.accent_color || "#2E5C55";
  return (
    <section
      className={`bg-white border border-[#E2E0DC] rounded-2xl overflow-hidden shadow-sm ${isComingSoon ? "opacity-80" : ""}`}
      data-testid={`nav-card-${block.block_id}`}
    >
      <div className="px-6 md:px-8 py-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-start gap-4">
          <div
            className="w-14 h-14 rounded-full flex items-center justify-center text-2xl flex-shrink-0"
            style={{ backgroundColor: `${accent}15` }}
            aria-hidden="true"
          >
            <span>{cfg.emoji || "✨"}</span>
          </div>
          <div>
            <h2
              className="font-['Playfair_Display'] text-2xl md:text-3xl font-semibold flex items-center gap-2"
              style={{ color: accent }}
            >
              {block.title}
            </h2>
            {block.quip && (
              <p className="text-sm md:text-base text-[#5C5C5C] mt-1 max-w-2xl leading-relaxed">
                {block.quip}
              </p>
            )}
          </div>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 px-6 md:px-8 py-3 bg-[#F9F8F6] border-t border-[#E2E0DC]">
        {cfg.show_embed && !isComingSoon ? (
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            aria-expanded={expanded}
            className="inline-flex items-center justify-center gap-2 text-sm font-bold uppercase tracking-wider rounded-full px-5 py-2.5 border-2 hover:bg-white"
            style={{ borderColor: accent, color: accent }}
          >
            {expanded ? "Collapse" : "Open the full page here"}
            <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? "rotate-180" : ""}`} />
          </button>
        ) : (
          <span className="inline-flex items-center justify-center gap-2 text-sm font-bold uppercase tracking-wider rounded-full px-5 py-2.5 border-2 opacity-60"
            style={{ borderColor: accent, color: accent }}>
            {isComingSoon ? "Coming Soon" : ""}
          </span>
        )}
        {!isComingSoon && cfg.route && (
          <Link
            to={cfg.route}
            className="inline-flex items-center gap-1.5 text-sm md:text-base font-bold uppercase tracking-wider text-[#2E5C55] hover:text-[#1A1A1A]"
          >
            {cfg.open_tab_label || "For best nav experience open here"}
            <ExternalLink className="h-4 w-4" />
          </Link>
        )}
        {cfg.cta_label && cfg.cta_route && (
          <Link
            to={cfg.cta_route}
            className="inline-flex items-center gap-1.5 text-xs md:text-sm font-bold uppercase tracking-wider hover:opacity-80"
            style={{ color: accent }}
          >
            {cfg.cta_label} →
          </Link>
        )}
      </div>
      {expanded && cfg.show_embed && !isComingSoon && cfg.route && (
        <div className="bg-white border-t border-[#E2E0DC]">
          <iframe
            src={`${cfg.route}?embed=1`}
            title={block.title}
            loading="lazy"
            className="w-full"
            style={{ height: "1600px", border: 0, display: "block" }}
          />
        </div>
      )}
    </section>
  );
};

export default NavCardEmbedBlock;
